import {
  PERMISSIONS,
  SECTIONS,
  PERMISSION_ACTIONS
} from 'constants/permissions';
import { USER_ROLES } from 'constants/users';
import { usePermissionsStateContext } from 'providers/Auth';
import { useCallback } from 'react';

import { checkPermission } from 'utils/permission';
import useIsSuperAdminRole from './useIsSuperAdminRole';

const ADMIN_SECTIONS: Record<string, number> = SECTIONS[USER_ROLES.admin];
const ADMIN_PERMISSIONS: Record<number, Record<string, number>> = PERMISSIONS[
  USER_ROLES.admin
];

const usePermission = () => {
  // context
  const permissions = usePermissionsStateContext();

  // Keyed on the super_admin ROLE, not on a user id. The backend has no
  // id-based superadmin check left, and id 1 is a different person in a
  // different database — a child company ships with its own.
  const isSuperAdmin = useIsSuperAdminRole();

  const checkUserPermission = useCallback(
    (routeKey: string | undefined, action = PERMISSION_ACTIONS.read) => {
      if (!routeKey) {
        return true;
      }

      const routeId = ADMIN_SECTIONS[routeKey];

      if (isSuperAdmin) return true;

      if (routeId === ADMIN_SECTIONS.super) {
        return false;
      }

      const value = ADMIN_PERMISSIONS[routeId][action];

      const permission = permissions[routeId];

      return !!permission && checkPermission(value, permission);
    },
    [isSuperAdmin, permissions]
  );

  const checkUserMultiPermission = useCallback(
    (key: string | string[] | undefined, action = PERMISSION_ACTIONS.read) =>
      Array.isArray(key)
        ? key.some(item => checkUserPermission(item, action))
        : checkUserPermission(key, action),
    [checkUserPermission]
  );

  return { checkUserPermission, checkUserMultiPermission, isSuperAdmin };
};

export default usePermission;
